import React from "react";
import {
  Autocomplete,
  TextField,
  Checkbox,
  Popper,
  Paper,
  ListItem,
  ListItemText,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Color tokens
const COLORS = {
  accent: "#009FDB",
  accentLight: "#E6F6FD",
  textPrimary: "#333333",
  placeholder: "#9CA3AF",
  border: "#D1D5DB",
  surface: "#FFFFFF",
};

export interface GenericOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface FilterMultiSelectProps {
  label?: string;
  placeholder?: string;
  options: GenericOption[];
  value: GenericOption[];
  onChange: (value: GenericOption[], reason?: string) => void;
  width?: number;
  disabled?: boolean;
  pluralLabel?: string;
}

const ALL_OPTION: GenericOption = {
  label: "All",
  value: "all",
};

const CustomPopper = (props: any) => {
  return <Popper {...props} style={{ zIndex: 1300 }} />;
};

export default function FilterMultiSelect({
  label = "Select",
  placeholder = "Search...",
  options,
  value,
  onChange,
  width = 442,
  disabled = false,
  pluralLabel
}: FilterMultiSelectProps) {

  const extendedOptions = options?.map(o => o.value)?.includes('all') ? options : [ALL_OPTION, ...options];

  const handleSelect = (e: any, newValue: GenericOption[], reason: string, details: any) => {
    if(reason === 'removeOption' && details?.option?.value === 'all'){
      return onChange([]);
    }
    if(reason === 'selectOption' && details?.option?.value === 'all'){
      return onChange(extendedOptions);
    }
    return onChange(newValue);
  };

  return (
    <Autocomplete
      disabled={disabled}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      multiple
      disableCloseOnSelect
      disableClearable
      options={extendedOptions}
      value={value}
      onChange={handleSelect}
      getOptionLabel={(option) => option.label}
      PopperComponent={CustomPopper}
      renderOption={(props, option, { selected }) => {
        const isSelected = selected || value.some((v) => v.value === option.value);

        return (
          <ListItem
            {...props}
            key={option.value}
            dense
            sx={{
              paddingY: "4px !important",
              backgroundColor: isSelected
                ? `${COLORS.accentLight} !important`
                : "transparent !important",
              borderLeft: isSelected
                ? `3px solid ${COLORS.accent} !important`
                : "3px solid transparent !important",
              "& .MuiListItemText-primary": {
                color: `${COLORS.textPrimary} !important`,
              },
            }}
          >
            <Checkbox
              checked={isSelected}
              icon={
                <CheckBoxOutlineBlankIcon
                  sx={{
                    width: "20px !important",
                    height: "20px !important",
                    color: `${COLORS.border} !important`,
                    backgroundColor: `${COLORS.surface} !important`,
                    borderRadius: "4px !important",
                    border: `2px solid ${COLORS.border} !important`,
                  }}
                />
              }
              checkedIcon={
                <CheckBoxIcon
                  sx={{
                    width: "20px !important",
                    height: "20px !important",
                    color: "#FFFFFF !important",
                    backgroundColor: `${COLORS.accent} !important`,
                    borderRadius: "4px !important",
                    padding: "2px !important",
                    boxSizing: "border-box !important",
                    border: "none !important",
                  }}
                />
              }
              sx={{
                marginRight: "12px !important",
                padding: "0 !important",
              }}
            />

            <ListItemText primary={option.label} />
          </ListItem>
        );
      }}
      renderTags={(tagValue) => {
        let displayText = '';
        if(tagValue.map(v => v.value)?.includes('all')){
          displayText = pluralLabel ? `All ${pluralLabel}` : 'All';
        } else {
          displayText = tagValue.map(option => option.label).join(', ');
        }
        return (
          <div
            style={{
              display: "flex",
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: COLORS.textPrimary,
              fontSize: "14px",
              paddingTop: "2px",
            }}
            title={displayText}
          >
            {displayText}
          </div>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? placeholder : ''}
          inputProps={{
            ...params.inputProps,
            readOnly: value.length > 0,
            style: {
              ...params.inputProps?.style,
              ...(value.length > 0 ? {
                width: 0,
                height: 0,
                padding: 0,
                border: 'none',
                opacity: 0,
                position: 'absolute',
              } : {})
            }
          }}
          InputLabelProps={{
            sx: {
              color: `${COLORS.placeholder} !important`,
              fontWeight: 400,
              "&.MuiInputLabel-root": {
                color: `${COLORS.placeholder} !important`,
                fontWeight: 400,
              },
              "&.Mui-focused": {
                color: `${COLORS.textPrimary} !important`,
                top: 5,
                fontSize: '20px',
                fontWeight: 400,
              },
              "&.MuiInputLabel-shrink": {
                color: `${COLORS.textPrimary} !important`,
                top: 5,
                fontSize: '20px',
                fontWeight: 400,
              },
              "&MuiAutocomplete-clearIndicator": {
                position: "absolute",
                top: "-45% !important",
              },
              background: `${COLORS.surface} !important`,
              paddingX: "4px !important",
              marginLeft: "-2px !important",
            },
          }}
          InputProps={{
            ...params.InputProps,
            sx: {
              "&.MuiOutlinedInput-root": {
                minHeight: "50px",
                paddingTop: "8px",
                paddingBottom: "8px",
                paddingRight: "40px",
              },
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: `${COLORS.surface} !important`,
              borderRadius: "4px !important",
              fontSize: "14px !important",
              color: `${COLORS.textPrimary} !important`,
              "& fieldset": {
                borderColor: `${COLORS.border} !important`,
              },
              "&:hover fieldset": {
                borderColor: `${COLORS.accent} !important`,
              },
              "&.Mui-focused fieldset": {
                borderColor: `${COLORS.accent} !important`,
              },
            },
          }}
        />
      )}
      PaperComponent={({ children }) => (
        <Paper
          elevation={4}
          sx={{
            borderRadius: "12px !important",
            backgroundColor: `${COLORS.surface} !important`,
            paddingY: "8px !important",
            width: `${width}px !important`,
            maxWidth: "100% !important",
          }}
        >
          {children}
        </Paper>
      )}
      popupIcon={
        <ExpandMoreIcon sx={{ color: `${COLORS.accent} !important` }} />
      }
      sx={{
        width: `${width}px !important`,
        maxWidth: "100% !important",
        overflow: "hidden !important",
      }}
    />
  );
}
